import { z } from 'zod';

/**
 * Device-local history of the clients, matters and invoices a person opened, for
 * the "Search or ask Bill" field's empty state. Holds opaque ids only (no names
 * or numbers), newest first. Deliberately kept across sign-out, so the key is per
 * account AND per user: another member of the same account who signs in on this
 * browser can open the same records, so an account-only key would show them what
 * a colleague had been looking at.
 */
export type RecentKind = 'client' | 'matter' | 'invoice';
export type RecentIds = Record<RecentKind, string[]>;

/** Whose history: recents are private to one user within one account. */
export interface RecentsOwner {
  accountId: string;
  userId: string;
}

export const RECENTS_PER_KIND = 8;

const KEY_PREFIX = 'bill_recents_v1_';

const RecentIdsSchema = z.object({
  client: z.array(z.uuid()),
  matter: z.array(z.uuid()),
  invoice: z.array(z.uuid()),
});

export function recentsStorageKey({ accountId, userId }: RecentsOwner): string {
  return `${KEY_PREFIX}${accountId}_${userId}`;
}

function empty(): RecentIds {
  return { client: [], matter: [], invoice: [] };
}

/** Never throws: missing, corrupt, wrongly shaped or unreadable storage reads as empty. */
export function readRecents(owner: RecentsOwner): RecentIds {
  try {
    const raw = localStorage.getItem(recentsStorageKey(owner));
    if (raw === null) return empty();
    const parsed = RecentIdsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : empty();
  } catch {
    return empty();
  }
}

function writeRecents(owner: RecentsOwner, recents: RecentIds): void {
  try {
    localStorage.setItem(recentsStorageKey(owner), JSON.stringify(recents));
  } catch (err) {
    console.error('Failed to persist recents to localStorage - history not saved', err);
  }
}

/** Moves `id` to the front of its kind (deduplicated) and drops the oldest beyond the cap. */
export function recordRecent(owner: RecentsOwner, kind: RecentKind, id: string): void {
  const recents = readRecents(owner);
  recents[kind] = [id, ...recents[kind].filter((existing) => existing !== id)].slice(0, RECENTS_PER_KIND);
  writeRecents(owner, recents);
}

/** Removes ids that no longer resolve (archived, deleted, no longer visible). */
export function forgetRecents(owner: RecentsOwner, kind: RecentKind, ids: readonly string[]): void {
  if (ids.length === 0) return;
  const recents = readRecents(owner);
  const gone = new Set(ids);
  recents[kind] = recents[kind].filter((id) => !gone.has(id));
  writeRecents(owner, recents);
}
